// ============================================================
// 認証済みユーザー用アバター + ドロップダウンメニュー
// ============================================================
//
// admin と mypage 両方のヘッダーで使用。
// クリックでメニュー展開:
//   - 名前 + email
//   - マイページ（admin にいるとき・自分が認証済み Player を持つ）
//   - 主催者画面（mypage にいるとき・自分が主催者権限あり）
//   - ログアウト
//
// 未認証時はメニューを表示しない（呼び出し側で nul チェック）
// 多子切替（Player 一覧）は将来の拡張で別UIに分離

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LogOut, ChevronDown, User as UserIcon, Settings, ShieldAlert, Check, Swords, BookOpen, UserCog, Crown, CalendarDays } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/services/api'
import { useOrganizerRegistration } from '@/hooks/useOrganizerRegistration'
import PlayerAvatar from '@/components/PlayerAvatar'
import type { MeActiveEvent } from '../../../shared/types'

type Props = {
  /** 現在のコンテキスト：admin 画面 / player マイページ / トップページ */
  context: 'admin' | 'mypage' | 'landing'
  /** アバターに使用する表示名（mypage ではプレイヤー名を渡す）。未指定なら User.name */
  displayName?: string
  /** マイページ等で当該 Player.avatarUrl を表示したい場合。null なら頭文字フォールバック */
  avatarUrl?:       string | null
  avatarUpdatedAt?: string | null
  /** 白/薄背景ヘッダー上に置く場合 true。トリガーの配色を濃色系へ切り替える（既定: 色背景前提の白系） */
  onLight?:         boolean
}

export default function UserAvatarMenu({ context, displayName, avatarUrl, avatarUpdatedAt, onLight = false }: Props) {
  const navigate  = useNavigate()
  const location  = useLocation()
  const user      = useAuthStore((s) => s.user)
  const logout    = useAuthStore((s) => s.logout)
  const [open, setOpen] = useState(false)
  const [isOrganizer, setIsOrganizer] = useState<boolean | null>(null)
  const [myPlayers, setMyPlayers]     = useState<{ id: number; name: string; discriminator: string; myPageToken: string; avatarUrl: string | null; avatarUpdatedAt: string | null }[]>([])
  const [activeEvents, setActiveEvents] = useState<MeActiveEvent[]>([])
  const ref = useRef<HTMLDivElement | null>(null)
  const { startRegistration, registering } = useOrganizerRegistration()

  // メニューを開いた瞬間に最新情報を取得
  useEffect(() => {
    if (!open || !user) return
    authApi.me()
      .then((u) => setIsOrganizer(u.isOrganizer))
      .catch(() => {})
    authApi.myPlayers()
      .then(setMyPlayers)
      .catch(() => {})
    authApi.activeEvents()
      .then(setActiveEvents)
      .catch(() => {})
  }, [open, user])

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open])

  if (!user) return null

  const initial = (displayName ?? user.name).slice(0, 1)
  const triggerAvatarSrc = avatarUrl
    ? `${avatarUrl}${avatarUpdatedAt ? `?v=${encodeURIComponent(avatarUpdatedAt)}` : ''}`
    : null

  const handleLogout = () => {
    setOpen(false)
    logout()
    // mypage はプレイヤー用ログインへ、admin は主催者ログインへ、landing はトップに留まる
    navigate(context === 'landing' ? '/' : context === 'mypage' ? '/player-login' : '/login', { replace: true })
  }

  const handleGotoAdmin = () => { setOpen(false); navigate('/events') }
  const handleGotoMyPage = (myPageToken: string) => { setOpen(false); navigate(`/player/${myPageToken}`) }
  const handleGotoSystemAdmin = () => { setOpen(false); navigate('/system-admin') }
  const handleGotoActiveEvent = (playerToken: string) => { setOpen(false); navigate(`/event/p/${playerToken}?preview=1`) }
  const handleGotoPublicEvents = () => { setOpen(false); navigate('/public/events') }
  const handleGotoAccountSettings = () => { setOpen(false); navigate('/settings?tab=account') }
  const isOnPublicEventList = location.pathname === '/public/events'

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full transition-colors ${
          onLight ? 'bg-neutral-100 hover:bg-neutral-200' : 'bg-white/15 hover:bg-white/25'
        }`}
        aria-label="ユーザーメニュー"
      >
        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold leading-none overflow-hidden ${
          onLight ? 'bg-primary text-white' : 'bg-white text-primary'
        }`}>
          {triggerAvatarSrc
            ? <img src={triggerAvatarSrc} alt="" className="w-full h-full object-cover" />
            : initial}
        </span>
        <ChevronDown size={12} className={onLight ? 'text-neutral-400' : 'text-white/80'} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-card border border-neutral-200 z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-neutral-100">
            <p className="text-sm font-semibold text-neutral-800 truncate">{user.name}</p>
            {user.email && <p className="text-xs text-neutral-400 truncate">{user.email}</p>}
          </div>

          {/* 開催中のイベント動線：自分が参加しているイベントの ongoing/closing が 1 件以上あるとき */}
          {activeEvents.length > 0 && (
            <div className="border-b border-neutral-100">
              <p className="px-3 pt-2 text-[10px] font-semibold text-neutral-400 uppercase tracking-wide">開催中</p>
              {activeEvents.map((ev) => {
                const isCurrent = ev.playerToken
                  ? location.pathname === `/event/p/${ev.playerToken}`
                  : false
                return (
                  <button
                    key={`${ev.eventId}-${ev.playerId}`}
                    type="button"
                    onClick={() => isCurrent
                      ? setOpen(false)
                      : ev.playerToken && handleGotoActiveEvent(ev.playerToken)}
                    disabled={!ev.playerToken}
                    className={
                      'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors disabled:opacity-50 ' +
                      (isCurrent ? 'bg-emerald-50 text-emerald-700' : 'text-neutral-700 hover:bg-neutral-50')
                    }
                  >
                    {isCurrent
                      ? <Check size={14} className="text-emerald-600 shrink-0" />
                      : <Swords size={14} className="text-primary shrink-0" />}
                    <span className="flex-1 truncate">{ev.eventName}</span>
                    {myPlayers.length > 1 && (
                      <span className="text-[10px] text-neutral-400 shrink-0">{ev.playerName}</span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {/* マイページ動線
              - 現在マイページに居て Player 1 件以下 → 切替不要なので非表示（現状を維持）
              - それ以外（admin 画面、申込ページ、イベントページ等）→ 1 件なら「マイページ」、2 件以上はリスト
              - 0 件 → 何も出さない */}
          {(() => {
            const matched = location.pathname.match(/^\/player\/([^/?#]+)/)
            const currentToken = matched ? decodeURIComponent(matched[1]) : null
            const isOnMyPagePath = currentToken !== null

            // 0 件 → 出さない
            if (myPlayers.length === 0) return null
            // マイページにいて 1 件以下 → 切替不要なので出さない
            if (isOnMyPagePath && myPlayers.length < 2) return null

            // 1 件 → 「マイページ」短縮表示
            if (myPlayers.length === 1) {
              return (
                <div className="border-b border-neutral-100">
                  <button
                    type="button"
                    onClick={() => handleGotoMyPage(myPlayers[0].myPageToken)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 text-left"
                  >
                    <UserIcon size={14} className="text-neutral-500 shrink-0" />
                    <span className="truncate">マイページ</span>
                  </button>
                </div>
              )
            }
            // 2件以上 → リスト表示
            return (
              <div className="border-b border-neutral-100">
                <p className="px-3 pt-2 text-[10px] font-semibold text-neutral-400 uppercase tracking-wide">マイページ</p>
                {myPlayers.map((p) => {
                  const isCurrent = currentToken === p.myPageToken
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => isCurrent ? setOpen(false) : handleGotoMyPage(p.myPageToken)}
                      className={
                        'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors ' +
                        (isCurrent ? 'bg-emerald-50 text-emerald-700' : 'text-neutral-700 hover:bg-neutral-50')
                      }
                    >
                      {isCurrent
                        ? <Check size={14} className="text-emerald-600 shrink-0" />
                        : <PlayerAvatar
                            name={p.name}
                            avatarUrl={p.avatarUrl}
                            avatarUpdatedAt={p.avatarUpdatedAt}
                            size={20}
                            className="text-[9px]"
                          />}
                      <span className="flex-1 truncate">{p.name}</span>
                      <span className="text-[10px] text-neutral-400 shrink-0">#{p.discriminator}</span>
                    </button>
                  )
                })}
              </div>
            )
          })()}

          {/* 主催者画面動線（mypage にいる時／system-admin にいる時 & 主催者権限あり） */}
          {isOrganizer && !location.pathname.startsWith('/events') && (
            <button
              type="button"
              onClick={handleGotoAdmin}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 text-left border-b border-neutral-100"
            >
              <Settings size={14} className="text-neutral-500 shrink-0" />
              <span className="truncate">主催者画面</span>
            </button>
          )}

          {/* 主催者登録動線（F-19: 主催者権限がないことが確定しているユーザーのみ） */}
          {isOrganizer === false && (
            <button
              type="button"
              onClick={() => { setOpen(false); void startRegistration() }}
              disabled={registering}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 text-left border-b border-neutral-100 disabled:opacity-50"
            >
              <Crown size={14} className="text-amber-500 shrink-0" />
              <span className="truncate">主催者登録する</span>
            </button>
          )}

          {/* システム管理（システム管理者権限あり、かつ現在 /system-admin に居ない） */}
          {user.isSystemAdmin && !location.pathname.startsWith('/system-admin') && (
            <button
              type="button"
              onClick={handleGotoSystemAdmin}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 text-left border-b border-neutral-100"
            >
              <ShieldAlert size={14} className="text-purple-500 shrink-0" />
              <span className="truncate">システム管理</span>
            </button>
          )}

          {/* ガイド（別タブで開く） */}
          <a
            href="/guide"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 text-left border-b border-neutral-100"
          >
            <BookOpen size={14} className="text-neutral-500 shrink-0" />
            <span className="truncate">ガイド</span>
          </a>

          {!isOnPublicEventList && (
            <button
              type="button"
              onClick={handleGotoPublicEvents}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 text-left border-b border-neutral-100"
            >
              <CalendarDays size={14} className="text-neutral-500 shrink-0" />
              <span className="truncate">公開イベント</span>
            </button>
          )}

          {/* アカウント設定（連携状況・名前変更・アカウント削除など） */}
          <button
            type="button"
            onClick={handleGotoAccountSettings}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 text-left border-b border-neutral-100"
          >
            <UserCog size={14} className="text-neutral-500 shrink-0" />
            <span className="truncate">アカウント設定</span>
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-rose-50 hover:text-rose-600 text-left"
          >
            <LogOut size={14} className="shrink-0" />
            <span>ログアウト</span>
          </button>
        </div>
      )}
    </div>
  )
}
